// Live-data preview for the 'multi-accordion' capture template.
//
// Vertical accordion of records — each section header shows the
// record's status + key; expanding reveals the full form (header +
// detail grids). One expanded section at a time (typical accordion
// UX). Backed by the same useEmbedRecords + livePreviewProcess
// pipeline as the other capture variants.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertCircle, ChevronDown, ChevronRight, FilePlus, Loader2,
  RefreshCw, Save, Trash2,
} from 'lucide-react'

import { embedLoadModel, embedRunAction, applyActionResultViaOnChange, makeEmbedUploader } from '@/livepreview/embedDataApi'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { useObjectVersion } from '@/store/objectVersionStore'

import { livePreviewDeploy } from '@/livepreview/api'
import {
  columnDbName, columnFormKey, mainSqlModel, parseApiError,
} from '@/livepreview/utils'
import FormRenderer, { fieldAnchorId } from '@/livepreview/runtime/FormRenderer'
import ErrorPanel from './embedTable/ErrorPanel'
import type { GridErrorItem } from './embedTable/gridTypes'
import {
  useEmbedRecords, type EmbedRecord,
} from '@/livepreview/useEmbedRecords'
import { useWarningConfirm } from '@twasta/page-runtime/txn'
import { useItemChange } from '@/livepreview/useItemChange'
import type { RecordStatus } from '@/livepreview/types'

import type {
  ColumnDef, SqlModel, TransactionModel,
} from '@/pages/designers/transaction/types'
import { GridPreviewView } from '@/pages/designers/transaction/PreviewView'
import type { EmbedConfig, PageInput } from './types'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  /** Site-mode routing for model + CRUD. */
  dataAccess?: DataAccess
  onSetInput?: (name: string, value: unknown) => void
}

const deployedTxns = new Map<string, Promise<void>>()
function deployKey(project: string, obj: string) { return `${project}::${obj}` }

async function ensureDeployed(project: string, obj: string): Promise<void> {
  const key = deployKey(project, obj)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, obj).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    deployedTxns.delete(key)
    throw err
  }
}

const STATUS_LABEL: Record<RecordStatus, string> = {
  retrieved: 'retrieved', dirty: 'edited', unsaved: 'new',
  saved: 'saved', failed: 'failed', partial: 'partial',
}
const STATUS_BG: Record<RecordStatus, string> = {
  retrieved: 'rgba(100,116,139,0.18)', dirty: 'rgba(234,179,8,0.20)',
  unsaved: 'rgba(59,130,246,0.18)', saved: 'rgba(16,185,129,0.20)',
  failed: 'rgba(239,68,68,0.20)', partial: 'rgba(249,115,22,0.20)',
}
const STATUS_FG: Record<RecordStatus, string> = {
  retrieved: '#475569', dirty: '#a16207', unsaved: '#1d4ed8',
  saved: '#047857', failed: '#b91c1c', partial: '#c2410c',
}

function StatusBadge({ status }: { status: RecordStatus }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: STATUS_BG[status], color: STATUS_FG[status],
    }}>{STATUS_LABEL[status]}</span>
  )
}

function SyncStatus({
  inSync, counts,
}: {
  inSync: boolean
  counts: Record<RecordStatus, number>
}) {
  if (inSync) {
    const total = counts.retrieved + counts.saved
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        fontSize: '0.8125rem', fontWeight: 600,
        background: 'rgba(16,185,129,0.18)', color: '#047857',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }} title="Local state matches the server">
        ● in sync{total > 0 ? ` · ${total}` : ''}
      </span>
    )
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600,
      background: 'rgba(234,179,8,0.18)', color: '#a16207',
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }} title="Local edits have not been pushed to the server">
      ● not synced
    </span>
  )
}

export default function EmbedCaptureAccordionPreview({
  project, embed, inputs: _inputs, inputValues, onSetInput,
  dataAccess,
}: Props) {
  const objName = embed.obj_name
  const txnVersion = useObjectVersion('T', project, objName || '')

  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    embedLoadModel(dataAccess, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, txnVersion, dataAccess])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  const details = useMemo<SqlModel[]>(() => {
    if (!model) return []
    return (model.sql_models || []).slice(1)
      .map(w => w?.sql_model)
      .filter((x): x is SqlModel => !!x)
  }, [model])

  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)

  // "Ignore warnings and proceed" confirm for business-logic warnings on save.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const records = useEmbedRecords({
    dataAccess,
    project, objName: objName || '', inputValues,
    filters: embed.filters || [], args: embed.args || [], recordVar: embed.recordVar,
    sm,
    confirmWarnings,
  })

  const filtersSig = useMemo(() => {
    const fs = embed.filters || []
    return JSON.stringify(fs.map((f) => {
      const ref = f.value
      let val: unknown
      if (ref?.source === 'page_input' && ref.name) val = inputValues[ref.name] ?? ''
      else if (ref) val = ref.value ?? ''
      let fromVal: unknown
      let toVal: unknown
      if (f.op === 'between') {
        if (f.from?.source === 'page_input' && f.from.name) fromVal = inputValues[f.from.name] ?? ''
        else if (f.from) fromVal = f.from.value ?? ''
        if (f.to?.source === 'page_input' && f.to.name) toVal = inputValues[f.to.name] ?? ''
        else if (f.to) toVal = f.to.value ?? ''
      }
      return { c: f.column, o: f.op, v: val, f: fromVal, t: toVal }
    }))
  }, [embed.filters, inputValues])

  const initialRetrieveDoneRef = useRef(false)
  useEffect(() => {
    if (!initialRetrieveDoneRef.current) {
      initialRetrieveDoneRef.current = true
      return
    }
    if (deployState !== 'deployed') return
    if (records.hasPendingChanges) return
    records.retrieve().catch(() => { /* surfaced via lastSaveError */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSig, deployState])

  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    // Site mode (public end-user page): schema already deployed by the author;
    // site users must not call the author-only deploy endpoint (it 401s).
    if (dataAccess?.mode === 'site') {
      setDeployState('deployed')
      void records.retrieve().catch(() => { /* non-fatal */ })
      return () => { cancelled = true }
    }
    setDeployState('deploying')
    setDeployError(null)
    ensureDeployed(project, objName)
      .then(async () => {
        if (cancelled) return
        setDeployState('deployed')
        try { await records.retrieve() } catch { /* non-fatal */ }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, dataAccess])

  // One expanded section at a time. Identified by record id so it
  // survives reorders / removes correctly.
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // One-shot restoration guard — see EmbedCaptureTabPreview for the
  // shared pattern. Only the first records-load arrival tries to honour
  // the saved selectedIdVar; subsequent user clicks are authoritative.
  const restoredRef = useRef(false)

  // Auto-expand on initial load. Prefer the record whose primaryKey
  // matches the saved selectedIdVar (a smart doc that was reopened)
  // so the accordion lands on the customer's previous selection
  // instead of always defaulting to the first item.
  useEffect(() => {
    if (records.records.length === 0) {
      restoredRef.current = false
      return
    }
    if (expandedId != null) return
    if (!restoredRef.current) {
      restoredRef.current = true
      const varName = embed.selectedIdVar
      if (varName) {
        const raw = inputValues[varName]
        const targetPk = raw == null || raw === '' ? null : String(raw)
        if (targetPk) {
          const rec = records.records.find((r) => r.primaryKey === targetPk)
          if (rec) {
            setExpandedId(rec.id)
            // Seed lastSelectionRef so writeSelectionVars(activeRec)
            // sees the echo and skips, preserving the saved var.
            lastSelectionRef.current = { id: targetPk, data: rec }
            return
          }
        }
      }
    }
    setExpandedId(records.records[0].id)
  }, [records.records, expandedId, embed.selectedIdVar, inputValues])

  const activeRec: EmbedRecord | null = useMemo(
    () => (expandedId ? records.records.find(r => r.id === expandedId) ?? null : null),
    [expandedId, records.records],
  )

  const lastSelectionRef = useRef<{ id: string | null; data: unknown }>({
    id: null, data: null,
  })
  const writeSelectionVars = useCallback((rec: EmbedRecord | null) => {
    if (!onSetInput) return
    const pk = rec?.primaryKey ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== pk) {
      onSetInput(embed.selectedIdVar, pk ?? '')
    }
    if (embed.selectedDataVar && last.data !== rec) {
      onSetInput(embed.selectedDataVar, rec ? rec.header : undefined)
    }
    lastSelectionRef.current = { id: pk, data: rec }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  useEffect(() => {
    writeSelectionVars(activeRec)
  }, [activeRec, writeSelectionVars])

  const triggerItemChange = useItemChange<{ recordId: string }>({
    dataAccess,
    project, objName: objName || '', sm,
    onUpdates: (ctx, updates) => {
      records.updateHeader(ctx.recordId, updates)
    },
  })

  const onFieldChange = useCallback((recId: string, name: string, v: unknown) => {
    const rec = records.records.find(r => r.id === recId)
    if (!rec) return
    const newHeader = { ...rec.header, [name]: v }
    records.updateHeader(recId, { [name]: v })
    triggerItemChange({ recordId: recId }, name, newHeader)
    if (expandedId === recId) {
      writeSelectionVars({ ...rec, header: newHeader })
    }
  }, [records, triggerItemChange, expandedId, writeSelectionVars])

  const handleAdd = useCallback(async () => {
    try {
      const rec = await records.addNew()
      setExpandedId(rec.id)
      writeSelectionVars(rec)
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Could not add new record: ${message}`)
    }
  }, [records, writeSelectionVars])

  const handleSaveAll = useCallback(async () => {
    const outcome = await records.saveBatch()
    if (outcome.success) {
      toast.success('All records saved')
    } else if (outcome.failedCount > 0) {
      toast.error(`${outcome.failedCount} record${outcome.failedCount === 1 ? '' : 's'} failed validation`)
    }
  }, [records])

  const handleRetrieve = useCallback(async () => {
    if (records.hasPendingChanges) return
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  const handleDiscardChanges = useCallback(async () => {
    const pending = records.counts.unsaved + records.counts.dirty +
      records.counts.failed + records.counts.partial
    if (!window.confirm(
      `Discard ${pending} pending change${pending === 1 ? '' : 's'} and reload from the server?`,
    )) return
    setExpandedId(null)
    records.discardLocalEdits()
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  const handleRemove = useCallback((id: string) => {
    const rec = records.records.find(r => r.id === id)
    if (!rec) return
    const msg = rec.status === 'unsaved'
      ? 'Remove this unsaved record?'
      : `Drop "${rec.primaryKey || ''}" from the local edit set (server copy stays)?`
    if (!window.confirm(msg)) return
    if (expandedId === id) setExpandedId(null)
    records.removeLocal(id)
  }, [records, expandedId])

  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => prev === id ? null : id)
  }, [])

  // ── Error side panel (aggregates errors across all accordion sections).
  // NOTE: these hooks MUST stay ABOVE the early returns below — `sm`
  // resolves from null → defined as the model loads, so a conditional
  // hook here would change the hook count between renders (React #310). ──
  const errorItems: GridErrorItem[] = useMemo(() => {
    const items: GridErrorItem[] = []
    records.records.forEach((rec, idx) => {
      const rowLabel = rec.primaryKey
        ? `Section — ${String(rec.primaryKey)}`
        : `Section — New ${idx + 1}`
      if (rec.recordError) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: null, columnLabel: null,
          message: rec.recordError,
          section: `Record ${idx + 1}`,
        })
      }
      for (const [field, msg] of Object.entries(rec.fieldErrors || {})) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: field,
          columnLabel: columnLabelFromSm(sm, field),
          message: msg,
          section: `Record ${idx + 1}`,
        })
      }
    })
    return items
  }, [records.records, sm])

  const [errorPanelOpen, setErrorPanelOpen] = useState(false)
  const errorCount = errorItems.length
  useEffect(() => {
    if (errorCount > 0 && records.lastSaveError) setErrorPanelOpen(true)
    if (errorCount === 0) setErrorPanelOpen(false)
  }, [errorCount, records.lastSaveError])

  const handleErrorSelect = useCallback((item: GridErrorItem) => {
    setExpandedId(item.rowKey)
    if (item.column) {
      requestAnimationFrame(() => {
        const el = document.getElementById(fieldAnchorId(item.column!))
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const input = el.querySelector<HTMLElement>(
          'input, select, textarea, button',
        )
        if (input) input.focus()
      })
    }
  }, [])

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null

  const pending = records.counts.unsaved + records.counts.dirty +
    records.counts.failed + records.counts.partial

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
          <SyncStatus inSync={!records.hasPendingChanges} counts={records.counts} />
          {deployState === 'deploying' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              deploying…
            </span>
          )}
          {deployState === 'error' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b91c1c)' }}>
              deploy failed: {deployError}
            </span>
          )}
        </div>
        {errorCount > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setErrorPanelOpen(true)}
            style={{ color: '#b91c1c' }}
            title="Show all validation errors across sections"
          >
            <AlertCircle size={14} /> {errorCount} error{errorCount === 1 ? '' : 's'}
          </button>
        )}
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleRetrieve}
          disabled={records.busy || deployState !== 'deployed' || records.hasPendingChanges}
          title={
            records.hasPendingChanges
              ? 'Local changes are not synced — Save or Discard them before refreshing'
              : 'Reload from the server'
          }
        ><RefreshCw size={14} /> Refresh</button>
        {records.hasPendingChanges && (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={handleDiscardChanges}
            disabled={records.busy || deployState !== 'deployed'}
            title="Drop pending changes and reload"
            style={{ color: '#b91c1c' }}
          ><Trash2 size={14} /> Discard changes</button>
        )}
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleAdd}
          disabled={records.busy || deployState !== 'deployed'}
        ><FilePlus size={14} /> Add section</button>
        <button
          type="button" className="btn btn-primary btn-sm"
          onClick={handleSaveAll}
          disabled={records.busy || deployState !== 'deployed' || pending === 0}
        >
          <Save size={14} /> {embed.captureSaveLabel || 'Save All'}
          {pending > 0 && (
            <span style={{ marginLeft: 4, opacity: 0.85 }}>({pending})</span>
          )}
        </button>
      </div>

      {records.hasPendingChanges && (
        <div style={{
          padding: '6px 10px', fontSize: '0.8425rem',
          background: 'rgba(234,179,8,0.10)',
          border: '1px solid rgba(234,179,8,0.40)',
          borderRadius: 6, color: '#a16207',
        }}>
          Local changes are not synced. Refresh is disabled until you{' '}
          <strong>{embed.captureSaveLabel || 'Save'}</strong> or{' '}
          <strong>Discard changes</strong>.
        </div>
      )}

      {records.lastSaveError && (
        <div style={{
          padding: '6px 10px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.8425rem',
        }}>{records.lastSaveError}</div>
      )}

      {/* Accordion */}
      {records.records.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {records.records.map((rec, i) => {
            const isOpen = expandedId === rec.id
            return (
              <div key={rec.id} style={{
                border: '1px solid var(--color-border)', borderRadius: 8,
                background: 'var(--color-bg)', overflow: 'hidden',
              }}>
                <button
                  type="button"
                  onClick={() => toggleExpand(rec.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', textAlign: 'left',
                    padding: '8px 14px', border: 'none',
                    background: isOpen ? 'var(--color-bg-secondary)' : 'transparent',
                    color: 'var(--color-text)', cursor: 'pointer',
                    borderBottom: isOpen ? '1px solid var(--color-border)' : 'none',
                  }}
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <strong style={{ fontSize: '0.9125rem' }}>
                    {rec.primaryKey
                      ? `${sm.form_title || objName} · ${rec.primaryKey.slice(0, 18)}${rec.primaryKey.length > 18 ? '…' : ''}`
                      : `${sm.form_title || objName} · New record ${i + 1}`}
                  </strong>
                  <StatusBadge status={rec.status} />
                </button>
                {isOpen && (
                  <RecordBody
                    rec={rec}
                    sm={sm}
                    details={details}
                    project={project}
                    objName={objName}
                    columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
                    onFieldChange={(name, v) => onFieldChange(rec.id, name, v)}
                    onRemove={() => handleRemove(rec.id)}
                    uploadDocument={makeEmbedUploader(dataAccess, project)}
                    dataAccess={dataAccess}
                  />
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{
          padding: '24px 16px', textAlign: 'center',
          color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)', borderRadius: 8,
        }}>
          No records yet. Click <strong>Add section</strong> to start a
          new record, or <strong>Refresh</strong> to load existing
          records.
        </div>
      )}

      <ErrorPanel
        open={errorPanelOpen}
        errors={errorItems}
        onClose={() => setErrorPanelOpen(false)}
        onSelect={handleErrorSelect}
        title="Save errors across sections"
      />
      {/* "Ignore warnings and proceed" confirm for save warnings. */}
      {warningsModal}
    </div>
  )
}

function RecordBody({
  rec, sm, details, project, objName, columnLabel,
  onFieldChange, onRemove, uploadDocument, dataAccess,
}: {
  rec: EmbedRecord
  sm: SqlModel
  details: SqlModel[]
  project: string
  objName: string
  columnLabel: (dbName: string) => string
  onFieldChange: (name: string, v: unknown) => void
  onRemove: () => void
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  dataAccess?: DataAccess
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {(rec.recordError || Object.keys(rec.fieldErrors).length > 0) && (
        <ValidationBanner
          fieldErrors={rec.fieldErrors}
          banner={rec.recordError}
          columnLabel={columnLabel}
        />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '6px 14px',
      }}>
        <button
          type="button"
          onClick={onRemove}
          className="btn btn-ghost btn-xs"
          title="Remove this record from the local edit set"
          style={{ color: '#b91c1c' }}
        ><Trash2 size={12} /> Remove section</button>
      </div>
      <div style={{ padding: '0 14px 14px' }}>
        <FormRenderer
          model={sm}
          values={rec.header}
          errors={rec.fieldErrors}
          onChange={onFieldChange}
          readOnlyKey={!!rec.primaryKey}
          project={project}
          objName={objName}
          uploadDocument={uploadDocument}
          runAction={(p, o, req) => embedRunAction(dataAccess, p, o, req)}
          formNo={sm.form_no}
          txnId={rec.primaryKey || undefined}
          onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
          onActionError={(err) => toast.error(parseApiError(err).message)}
        />
      </div>
      {details.length > 0 && (
        <div style={{
          padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
        }}>
          {details.map((d, di) => (
            <div key={di} style={{
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-bg)', overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 10px', fontSize: '0.8125rem',
                background: 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border)',
              }}>
                <span style={{ fontWeight: 600 }}>
                  {d.form_title || `Detail ${di + 1}`}
                </span>
                <span style={{
                  padding: '1px 6px', borderRadius: 8, fontSize: '0.8125rem',
                  background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
                }}>Detail grid</span>
              </div>
              <GridPreviewView form={d} sampleRows={2} />
            </div>
          ))}
          <div style={{
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            fontStyle: 'italic',
          }}>
            Detail-row editing is not yet wired in the designer preview;
            the runtime captures detail rows for this record.
          </div>
        </div>
      )}
    </div>
  )
}

function ValidationBanner({
  fieldErrors, banner, columnLabel,
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
}) {
  const keys = Object.keys(fieldErrors)
  if (keys.length === 0 && !banner) return null
  return (
    <div style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444', fontSize: '0.8425rem',
    }}>
      <div style={{ fontWeight: 600, marginBottom: keys.length ? 4 : 0 }}>
        {keys.length > 0
          ? `Save blocked — ${keys.length} validation error${keys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {keys.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {keys.map((k) => (
            <li key={k}>
              <strong>{columnLabel(k)}</strong>: {fieldErrors[k]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function columnLabelFromSm(sm: SqlModel | null, key: string): string {
  if (!sm?.columns) return key
  // ``key`` may be a form_key (modern validation) or a db_name (PG-error
  // fallback). Match against both.
  for (const wrap of sm.columns) {
    const col = wrap.column as ColumnDef | undefined
    if (!col) continue
    if (columnFormKey(col) === key || columnDbName(col) === key) {
      return col.heading || col.name || key
    }
  }
  return key
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
  color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
}
